import React, { ReactElement } from 'react'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import CartReviewTileImage from './_CartReviewTileImage'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import NestableClickable from 'components/Common/NestableClickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const Container = styled(NestableClickable)`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 200ms;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

/**
 * This should be a tertiary button but the hover colour
 * is the same colour as the tile itself so it blends in
 * A hacky workaround for now is to make it a secondary button
 * (with a dark hover) with no border to make it look like
 * a tertiary.
 */
const DodgyButton = styled(IconButton)`
  border: none;
`

const Element = styled.div`
  display: grid;
  grid-template-columns: ${rem(64)} 1fr;
  grid-template-areas: 'image content';
  gap: ${rem(16)};
`

const Content = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: 0; /* Allow content to shrink below its min-content size */
`

interface Props {
  imageElement?: ReactElement
  banner: React.ReactNode
  label?: ReactElement
  price?: React.ReactNode
  quantity?: React.ReactNode
  to?: string
  content: ReactElement
  onDelete?: React.MouseEventHandler<HTMLButtonElement>
}

function CondensedCartReviewTile({
  imageElement,
  banner,
  label,
  price,
  quantity,
  to,
  onDelete,
  content,
}: Props) {
  return (
    <Container to={to} target="_blank">
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" gap={16} fullWidth>
          <Element>
            {imageElement && <CartReviewTileImage
              imageElement={imageElement}
              label={label}
            />}
            <Content>
              {content}
              {price}
              {quantity}
            </Content>
          </Element>
          {onDelete && (<DodgyButton
            kind="secondary"
            horizontalOutdent="end"
            verticalOutdent="start"
            onClick={onDelete}
          >
            <LineTrashAltIcon />
          </DodgyButton>
          )}
        </Group>
        {banner}
      </VerticalSpacer>
    </Container>
  )
}

export default CondensedCartReviewTile
